// components/ParallaxBanner.tsx
'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { ColourfulText } from './colourful-text';
import Link from 'next/link';
import { Button } from './moving-border';
import type { BannerContent, LocaleCode } from '@/types/sections';
import { pickLocalizedString } from '@/lib/locale';
import { getLocalizedHref } from '@/lib/site';
import Image from 'next/image';

interface ParallaxBannerProps {
  banner: BannerContent;
  locale: LocaleCode;
}

const ParallaxBanner: React.FC<ParallaxBannerProps> = ({ banner, locale }) => {
  const text = pickLocalizedString(banner.text, locale);
  const colored = pickLocalizedString(banner.colored, locale);
  const cta = pickLocalizedString(banner.ctaLabel, locale, 'Join The Club →');
  const href = getLocalizedHref(locale, banner.ctaHref || '/#join');
  const height = banner.height || 'h-60';
  const src = banner.imageUrl || '/images/sliders/1.webp';
  return (
    <div
      className={cn(
        'w-full relative flex items-center justify-center overflow-hidden',
        height
      )}
    >
      <Image
        src={src}
        alt={text || 'Uncommon 12 banner'}
        fill
        sizes='100vw'
        className='absolute inset-0 z-0 h-full w-full object-cover'
        loading='lazy'
        decoding='async'
        fetchPriority='low'
      />
      {/* Overlay */}
      <div className='absolute inset-0 bg-black/40 dark:bg-black/60 z-10' />

      {/* Content */}
      <div className='relative z-20 text-center px-4 text-white'>
        <h2 className='text-2xl md:text-4xl font-semibold mb-4'>
          {text} <ColourfulText text={colored} />
        </h2>
        <Link href={href}>
          <Button>{cta}</Button>
        </Link>
      </div>
    </div>
  );
};

export default ParallaxBanner;
